/**
 * Навигация рабочего места по макету-эталону (docs/design/frontend-v2, prototype.js):
 * четыре группы и их пункты. Адреса — прежние маршруты приложения: глубокие ссылки
 * и закладки пользователей продолжают работать.
 */

import type { WorkspaceIconName } from '@/components/workspace/WorkspaceIcon';

export interface NavItem {
	/** Раздел: по нему каркас помнит последний список раздела для хлебных крошек */
	id: string;
	to: string;
	labelKey: string;
	icon: WorkspaceIconName;
	/** Пункт виден только при включённой функции установки */
	feature?: 'kubehound';
	/** Счётчик пункта; без данных счётчик не показывается */
	counter?: 'storages' | 'events';
}

export interface NavGroup {
	labelKey: string;
	items: NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
	{
		labelKey: 'nav:groupLabels.workspace',
		items: [
			{ id: 'overview', to: '/overview', labelKey: 'nav:overview', icon: 'overview' },
			{ id: 'dataMap', to: '/data-map', labelKey: 'nav:dataMap', icon: 'map' },
		],
	},
	{
		labelKey: 'nav:groupLabels.data',
		items: [
			{ id: 'services', to: '/service-catalog', labelKey: 'nav:services', icon: 'box' },
			{ id: 'apiEndpoints', to: '/api-endpoints', labelKey: 'nav:apiEndpoints', icon: 'api' },
			{
				id: 'dataStores',
				to: '/data-storages',
				labelKey: 'nav:dataStores',
				icon: 'database',
				counter: 'storages',
			},
			{ id: 'dataTypes', to: '/data-types', labelKey: 'nav:dataTypes', icon: 'tags' },
			{ id: 'groups', to: '/asset-groups', labelKey: 'nav:groups', icon: 'groups' },
		],
	},
	{
		labelKey: 'nav:groupLabels.protection',
		items: [
			{
				id: 'events',
				to: '/activity-log',
				labelKey: 'nav:events',
				icon: 'alert',
				counter: 'events',
			},
			{ id: 'policies', to: '/policies', labelKey: 'nav:policies', icon: 'shield' },
			{
				id: 'attackPaths',
				to: '/attack-paths',
				labelKey: 'nav:attackPaths',
				icon: 'path',
				feature: 'kubehound',
			},
			{ id: 'llmGate', to: '/llm-gate', labelKey: 'nav:llmGate', icon: 'ai' },
		],
	},
	{
		labelKey: 'nav:groupLabels.management',
		items: [
			{ id: 'sensors', to: '/infrastructure-access', labelKey: 'nav:sensors', icon: 'sensor' },
			{ id: 'integrations', to: '/integrations', labelKey: 'nav:integrations', icon: 'plug' },
			{ id: 'reports', to: '/reports', labelKey: 'nav:reports', icon: 'report' },
			{ id: 'team', to: '/team', labelKey: 'nav:team', icon: 'team' },
		],
	},
];

export type PageSection = Pick<NavItem, 'id' | 'to' | 'labelKey'>;

/** Страницы вне меню, у которых есть своё имя в хлебных крошках */
const SECONDARY_PAGES: readonly PageSection[] = [
	{ id: 'admin', to: '/admin', labelKey: 'nav:admin' },
	{ id: 'account', to: '/account', labelKey: 'nav:account' },
	{ id: 'changePassword', to: '/change-password', labelKey: 'nav:changePassword' },
	{ id: 'copilot', to: '/ai-copilot', labelKey: 'nav:copilot' },
	{ id: 'learning', to: '/learning', labelKey: 'nav:learning' },
];

function matches(pathname: string, to: string): boolean {
	return pathname === to || pathname.startsWith(`${to}/`);
}

/** Раздел текущей страницы для хлебных крошек; без имени — общий раздел */
export const UNNAMED_PAGE: PageSection = { id: 'page', to: '/', labelKey: 'nav:page' };

export function pageSection(pathname: string): PageSection {
	const pages: PageSection[] = [...NAV_GROUPS.flatMap((group) => group.items), ...SECONDARY_PAGES];
	const found = pages
		.filter((page) => matches(pathname, page.to))
		.sort((a, b) => b.to.length - a.to.length)[0];
	return found ?? UNNAMED_PAGE;
}
